import type { OfferFull, OfferMedium } from '~/types/Catalog';
import type { CartItem } from '~/types/Cart';
import type { OfferInOrder } from '~/types/Profile';
import { isRuntimeFlagEnabled } from '~/utils/runtimeFlag';

function getOfferProductName(offer: OfferFull | OfferMedium | OfferInOrder | CartItem['data']): string {
  return 'product_name_with_offer_properties' in offer
    ? offer.product_name_with_offer_properties
    : offer.product_name || offer.offer_name || 'Товар из заказа';
}

export function useGtmModule() {
  const route = useRoute();
  const profileStore = useProfileStore();

  function getUserId() {
    return profileStore.userDetails?.user_id ?? null;
  }

  async function canSendYandexMetrikaEcommerce() {
    if (!import.meta.client || !isRuntimeFlagEnabled(useRuntimeConfig().public.isYandexMetrikaEcommerceEnabled))
      return false;

    if (profileStore.userInfoLoadStatus !== 'resolved') await profileStore.getUserInfo();

    if (profileStore.userInfoLoadStatus !== 'resolved') return false;

    return profileStore.userDetails?.analytics?.yandex_metrika_ecommerce_enabled !== false;
  }

  async function getYandexMetrikaClientId(): Promise<string | null> {
    if (!(await canSendYandexMetrikaEcommerce())) return null;

    const { yandexMetrikaId } = useRuntimeConfig().public;

    const ym = window.ym;
    if (!yandexMetrikaId || !ym) return null;

    return new Promise((resolve) => {
      const timeout = window.setTimeout(() => resolve(null), 1000);

      try {
        ym.call(window, yandexMetrikaId, 'getClientID', (clientId: string) => {
          window.clearTimeout(timeout);
          resolve(clientId || null);
        });
      } catch {
        window.clearTimeout(timeout);
        resolve(null);
      }
    });
  }

  async function pushEcommerceToDataLayer(
    eventName: string,
    items: Record<string, any>[],
    additionalFields?: Record<string, any>,
  ) {
    if (!(await canSendYandexMetrikaEcommerce())) return;

    window.dataLayer?.push({ ecommerce: null });

    const data = additionalFields
      ? { event: eventName, ecommerce: { items }, ...additionalFields }
      : { event: eventName, ecommerce: { items } };

    window.dataLayer?.push(data);
  }

  function pushEventToDataLayer(eventName: string, fields?: Record<string, any>) {
    if (!import.meta.client) return;

    window.dataLayer?.push({ event: eventName, ...(fields ?? {}) });
  }

  async function sendBannerEventToDataLayer(eventName: string | 'view_promotion' | 'select_promotion', banner: any) {
    pushEventToDataLayer(eventName, banner);
  }

  async function sendOfferViewEventToDataLayer(eventName: string | 'view_item', offer: OfferFull) {
    await pushEcommerceToDataLayer(eventName, [
      {
        item_id: offer.id,
        item_name: offer.product_name,
        item_brand: offer.brand.name,
        item_category: offer.breadcrumbs?.at(-2)?.title,
        item_list_name: route.name,
        item_variant: offer.displayable_offer_properties?.length ? offer.displayable_offer_properties[0].value : null, // Передаем цвет товара
        item_index: 1,
        price: offer.price,
        quantity: 1,
      },
    ]);
  }

  async function sendOfferEventToDataLayer(
    eventName: string | 'select_item' | 'add_to_cart' | 'remove_from_cart' | 'add_to_wishlist' | 'remove_from_wishlist',
    offer: OfferMedium | OfferFull | CartItem['data'],
    index?: number,
  ) {
    const additionalFields =
      eventName === 'add_to_cart' ||
      eventName === 'remove_from_cart' ||
      eventName === 'add_to_wishlist' ||
      eventName === 'remove_from_wishlist'
        ? { user_id: getUserId() }
        : undefined;

    const itemCategory =
      'breadcrumbs' in offer
        ? offer.breadcrumbs?.at(-2)?.title
        : route.name === 'catalog-slug'
          ? route.params.slug[route.params.slug.length === 1 ? 0 : 1]
          : null;

    await pushEcommerceToDataLayer(
      eventName,
      [
        {
          item_id: offer.id,
          item_name: getOfferProductName(offer),
          item_brand: offer.brand?.name ?? null,
          item_category: itemCategory,
          item_list_name: route.name,
          item_variant: offer.displayable_offer_properties?.length ? offer.displayable_offer_properties[0].value : null,
          item_index: typeof index === 'number' ? index + 1 : null,
          price: offer.price,
          quantity: 1,
        },
      ],
      additionalFields,
    );
  }

  function prepareOffersMediumForDataLayer(offers: (OfferMedium | CartItem['data'])[], includeIndex = false) {
    const items = [];

    for (const [index, offer] of offers.entries()) {
      items.push({
        item_id: offer.id,
        item_name: offer.product_name_with_offer_properties,
        item_brand: offer.brand?.name ?? null,
        item_category: route.name === 'catalog-slug' ? route.params.slug[route.params.slug.length === 1 ? 0 : 1] : null,
        item_list_name: route.name,
        item_variant: offer.displayable_offer_properties?.length ? offer.displayable_offer_properties[0].value : null,
        item_index: includeIndex ? index + 1 : null,
        price: offer.price,
        quantity: offer.count ?? 1,
      });
    }

    return items;
  }

  async function sendPageViewOffersEventToDataLayer(
    eventName: string | 'view_item_list' | 'view_cart' | 'begin_checkout',
    offers: (OfferMedium | CartItem['data'])[],
    additionalFields?: Record<string, any>,
  ) {
    await pushEcommerceToDataLayer(eventName, prepareOffersMediumForDataLayer(offers, true), additionalFields);
  }

  function sendCheckoutPaymentSelectEventToDataLayer() {
    pushEventToDataLayer('add_payment_info', { user_id: getUserId() });
  }

  function sendCheckoutShippingSelectEventToDataLayer() {
    pushEventToDataLayer('add_shipping_info', { user_id: getUserId() });
  }

  async function sendShareCartEventToDataLayer(offers: (OfferMedium | CartItem['data'])[]) {
    await pushEcommerceToDataLayer('share_cart', prepareOffersMediumForDataLayer(offers, true), {
      user_id: getUserId(),
    });
  }

  function prepareOrderOffersForDataLayer(offers: OfferInOrder[]) {
    const items = [];

    for (const offer of offers) {
      items.push({
        item_id: offer.id,
        item_name: offer.product_name,
        price: offer.price,
        quantity: offer.count,
      });
    }

    return items;
  }

  async function sendPaymentInAccountEventToDataLayer(offers: OfferInOrder[]) {
    await pushEcommerceToDataLayer('payment_in_account', prepareOrderOffersForDataLayer(offers));
  }

  async function sendPromoCodeEventToDataLayer(
    eventName: string | 'coupon_apply' | 'coupon_applied',
    promoCode: string,
  ) {
    pushEventToDataLayer(eventName, { user_id: getUserId(), coupon: promoCode });
  }

  function sendLoginEventToDataLayer(method: string) {
    pushEventToDataLayer('login', { method, user_id: getUserId() });
  }

  function sendSearchEventToDataLayer(term: string) {
    pushEventToDataLayer('search', { search_term: term, user_id: getUserId() });
  }

  function sendView404EventToDataLayer(referrer: string | null) {
    pushEventToDataLayer('view_404', { Referrer: referrer });
  }

  async function sendShareOfferEventToDataLayer(offer: OfferMedium | OfferFull | CartItem['data'], method: string) {
    await pushEcommerceToDataLayer(
      'share_offer',
      [
        {
          item_id: offer.id,
          item_name: getOfferProductName(offer),
          item_brand: offer.brand?.name ?? null,
          item_category:
            'breadcrumbs' in offer
              ? offer.breadcrumbs?.at(-2)?.title
              : route.name === 'catalog-slug'
                ? route.params.slug[route.params.slug.length === 1 ? 0 : 1]
                : null,
          item_list_name: route.name,
          item_variant: offer.displayable_offer_properties?.length ? offer.displayable_offer_properties[0].value : null,
          item_index: 1,
          price: offer.price,
          quantity: 1,
        },
      ],
      { user_id: getUserId(), method },
    );
  }

  async function sendViewReviewsEventToDataLayer(offer: OfferFull) {
    await pushEcommerceToDataLayer('view_reviews', [
      {
        item_id: offer.id,
        item_name: offer.product_name,
        item_brand: offer.brand.name,
        item_category: offer.breadcrumbs?.at(-2)?.title,
        item_list_name: route.name,
        item_variant: offer.displayable_offer_properties?.length ? offer.displayable_offer_properties[0].value : null,
        item_index: 1,
        price: offer.price,
        quantity: 1,
      },
    ]);
  }

  return {
    sendBannerEventToDataLayer,
    sendPageViewOffersEventToDataLayer,
    sendOfferViewEventToDataLayer,
    sendOfferEventToDataLayer,
    sendCheckoutPaymentSelectEventToDataLayer,
    sendCheckoutShippingSelectEventToDataLayer,
    getYandexMetrikaClientId,
    sendShareCartEventToDataLayer,
    sendPaymentInAccountEventToDataLayer,
    sendPromoCodeEventToDataLayer,
    sendLoginEventToDataLayer,
    sendSearchEventToDataLayer,
    sendView404EventToDataLayer,
    sendShareOfferEventToDataLayer,
    sendViewReviewsEventToDataLayer,
  };
}
